import { useCallback, useEffect, useRef, useState } from "react";

import { CepAddress, lookupCep } from "../../utils/viaCep";

/** idle; loading; notFound: the CEP doesn't exist; unavailable: ViaCEP couldn't be asked (down, offline). */
export type CepStatus = "idle" | "loading" | "notFound" | "unavailable";

type Handlers = {
  /** The address of the CEP. */
  onFound: (address: CepAddress) => void;
  /** No address came (the CEP doesn't exist, or ViaCEP couldn't be asked): the address is typed by hand. */
  onMiss: () => void;
};

/**
 * The address of a Brazilian CEP, from ViaCEP, for the forms that ask for one. `search` takes what is typed: with
 * fewer than 8 digits nothing is asked (and a lookup on its way is dropped); the answer of an older CEP never lands
 * after a newer one. The status tells the form what to say; `onMiss` is where it clears what the CEP before had filled.
 */
export const useCepLookup = () => {
  const [status, setStatus] = useState<CepStatus>("idle");
  const controller = useRef<AbortController>();

  useEffect(() => () => controller.current?.abort(), []);

  const search = useCallback((typed: string, { onFound, onMiss }: Handlers) => {
    controller.current?.abort();
    const cep = typed.replace(/\D/g, "");
    if (cep.length !== 8) {
      setStatus("idle");
      return;
    }
    const own = new AbortController();
    controller.current = own;
    setStatus("loading");
    lookupCep(cep, own.signal)
      .then(address => {
        if (!address) {
          setStatus("notFound");
          onMiss();
          return;
        }
        setStatus("idle");
        onFound(address);
      })
      .catch(error => {
        if (error?.name === "AbortError") return;
        setStatus("unavailable");
        onMiss();
      });
  }, []);

  return { status, search };
};
